import React, { useEffect, useRef, useState } from 'react';
import {AnimatePresence, motion} from "framer-motion";
import SimpleBar from 'simplebar-react';
import { debounce } from "lodash"

import { IconLibrary, ThriveIcon } from 'thrive-components';

import useDebounce from  '@/components/hooks/useDebounce';
import useDetectClick from  '@/components/hooks/useDetectClick';
import UserResult from './UserResult';
import GroupResult from './GroupResult';

import useHTTP from  '@/components/hooks/useHTTP';
import { BuildPath } from  '@/helpers/Paths';
import { user, group } from  '@/interfaces/planner';


import '../../../css/invitees.scss'

const Invitees = (
    {setAllSelectedGroups, setAllSelectedUsers, setFormTouched, calendarId} : 
    {setAllSelectedGroups:(groups:group[]) => void, setAllSelectedUsers:(user:user[]) => void,  setFormTouched?:(touched:boolean) => void, calendarId: number }) => {

    // Groups
    const [groupShowSearch, setGroupShowSearch] = useState<boolean>(false)
    const [groupSearchInput, setGroupSearchInput] = useState<string>("");
    const [groups, setGroups] = useState<group[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<group[]>([]);
    const [groupsTotalUsers, setGroupsTotalUsers] = useState<number>(0);
    const [groupsSkip, setGroupsSkip] = useState<number>(0);
    const nextGroupsSkip = useRef<number>(0);

    // Users
    const [userShowSearch, setUserShowSearch] = useState<boolean>(false)
    const [userSearchInput, setUserSearchInput] = useState<string>("");
    const [users, setUsers] = useState<user[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<user[]>([]);
    const [usersSkip, setUsersSkip] = useState<number>(0);
    const nextUsersSkip = useRef<number>(0);
    const lastGroup = useRef<string>('')
    const lastUser = useRef<string>('')

    const groupsDebounce = useDebounce(groupSearchInput, 500);
    const userDebounce = useDebounce(userSearchInput, 500);

    const [searchingGroups, setSearchingGroups]  = useState<boolean>(false)
    const [searchingUsers, setSearchingUsers]  = useState<boolean>(false)

    const isMounted = useRef<boolean>(true);

    const {get} = useHTTP();

    const aniFade = 0.3;
    const globalSkip = 40;

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        }
    }, [])

    // Move up state
    useEffect(() => {
        setAllSelectedGroups(selectedGroups)
        setAllSelectedUsers(selectedUsers)
        
        // Form touched?
        if (selectedGroups.length || selectedUsers.length){
            setFormTouched?.(true)
        }

    }, [selectedGroups, selectedUsers])
    
    // Build user count for groups
    useEffect(() => {

        const count = selectedGroups.reduce((count, curr) => {
            return count + curr.totalUsersInGroup
        }, 0);

        setGroupsTotalUsers(count)

    }, [selectedGroups])

    useEffect(() => {
        if (groupShowSearch){

            setSearchingGroups(true)

            const skip = groupsDebounce === lastGroup.current ? nextGroupsSkip.current : 0

            get(`search/org/groups?searchTerm=${groupsDebounce}&skip=${skip}&top=${globalSkip}&calendarId=${calendarId}`).then(({data}:{data:any}) => {
                if (data.groups){
                    // New search
                    if (!skip){
                        setGroups(data.groups)  
                    }
                    // Append
                    else{
                        setGroups(x => [...x, ...data.groups])  
                    }     
 
                    nextGroupsSkip.current = nextGroupsSkip.current + globalSkip;
                }

            }).catch(err => {
                console.log(err, "err")
            })
            .finally(() => {

                lastGroup.current = groupsDebounce; 
                setSearchingGroups(false)
            })
        }   

    }, [groupsDebounce, groupsSkip, groupShowSearch])

    useEffect(() => {

        if (userShowSearch){

            setSearchingUsers(true)

            const skip = userDebounce === lastUser.current ? nextUsersSkip.current : 0

            get(`search/org/users?searchTerm=${userDebounce}&skip=${skip}&top=${globalSkip}&calendarId=${calendarId}`).then(({data}:{data:any}) => {

                if(data.users){
                    // New search
                    if (!skip){
                        setUsers(data.users) 
                    }
                    // Append
                    else{
                        setUsers(x => [...x, ...data.users])  
                    }  
                    
                    // Set next in ref
                    nextUsersSkip.current = nextUsersSkip.current + globalSkip;
                }
            }).catch(err => {
                console.log(err, "err")
            })
            .finally(() => {

                lastUser.current = userDebounce;
                
                setSearchingUsers(false)   
            })
        }   

    }, [userDebounce, usersSkip, userShowSearch])

    // Close users on group click
    useEffect(() => {

        if(userShowSearch && groupShowSearch){
            setUserShowSearch(false)
        }

    }, [groupShowSearch, userShowSearch])

    // Close users & groups auto-complete on outside click
    useDetectClick("search-input", (clickedInInviteBox) => {

        if (!clickedInInviteBox){
            closeAll()
        }
    }, true)

    function closeAll(){

        // Groups
        setGroupShowSearch(false)

        setTimeout(() => {
            if (isMounted.current){
                setGroupSearchInput("")
                setGroups([])
                setGroupsSkip(0)
                nextGroupsSkip.current = 0
            }

        }, aniFade);

        // Users
        setUserShowSearch(false)

        setTimeout(() => {
            if (isMounted.current){
                setUserSearchInput("")
                setUsers([])
                setUsersSkip(0) 
                nextUsersSkip.current = 0
            }
        }, aniFade);
    }

    function scrollHandler (event:any, groups:boolean) {

        event.persist()
        debouncedScroll(event, groups)
    }

    function handleOnFocusUsers(){
        // Reset on a new click
        nextUsersSkip.current = 0;
        setUserShowSearch(true)
        setGroupShowSearch(false)
    }

    function handleOnFocusGroups(){
        // Reset on a new click
        nextGroupsSkip.current = 0;
        setGroupShowSearch(true)
        setUserShowSearch(false)
    }

    const debouncedScroll =  React.useRef(
        
        debounce((event,groups) => {
    
            const {scrollHeight, scrollTop, clientHeight} = event.target;
            const isBottomReached = (scrollHeight - Math.round(scrollTop) <= clientHeight + 15);
        
            if (isBottomReached) {

                if (groups){
                    setGroupsSkip(x => x + 10)
                }
                else{
                    setUsersSkip(x => x + 10)
                }
            }
        }, 500)

    ).current;

    return (
        <div className='invitees'>

            <h2>Invitees ({selectedUsers.length + groupsTotalUsers})</h2>

            <div className='search-input'>
  
                <div className='input-container'>
                    <ThriveIcon className="input-icon" iconName={IconLibrary.SEARCH_GLASS} color='var(--thriveGreyDark)' sizeInPx='14'/>

                    <input 
                        type="text" 
                        className="search-user-groups" 
                        placeholder='Search User Groups'
                        aria-label='Search User Groups'  
                        value={groupSearchInput}
                        onFocus={handleOnFocusGroups}
                        onChange={e => setGroupSearchInput(e.target.value)}    
                    />

                    {searchingGroups &&  
                        <img className="spinner loading" src={`${BuildPath()}/images/spinner.gif`} alt="loading..." />
                    } 

                    {!searchingGroups && groupShowSearch && groups.length > 0 && 
                        <img onClick={closeAll} className="close-results" src={`${BuildPath()}/images/buttons/x-icon.svg`} alt="Close" />
                    }
                    <AnimatePresence>
                        {groupShowSearch && groups.length > 0 && (
                            <motion.div
                                className={"search-results search-results-groups"}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: '340px' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: aniFade }}
                            >
                                <SimpleBar scrollableNodeProps={{onScroll: (e: any) => scrollHandler(e, true)}} style={{ maxHeight: 530 }}>
                                    <ul>
                                        {groups.map(group => {
                                            return (
                                                <GroupResult
                                                    group={group}
                                                    selectedGroups={selectedGroups}
                                                    setSelectedGroups={setSelectedGroups}
                                                    key={`group-${group.id}`} />
                                            )
                                        })}
                                    </ul>
                                </SimpleBar>

                            </motion.div>
                        )}

                    </AnimatePresence>
    
                </div>
                
                <div className='input-container'>
                    <ThriveIcon className="input-icon" iconName={IconLibrary.SEARCH_GLASS} color='var(--thriveGreyDark)' sizeInPx='14'/>

                    <input 
                        type="text" 
                        placeholder='Search Users'
                        aria-label='Search Users'
                        value={userSearchInput}  
                        onFocus={handleOnFocusUsers} 
                        onChange={e => setUserSearchInput(e.target.value)} 
                    />

                    {searchingUsers && 
                        <img className="spinner loading" src={`${BuildPath()}/images/spinner.gif`} alt="loading..." />
                    }

                    {!searchingUsers && userShowSearch && users.length > 0 && 
                        <img onClick={closeAll} className="close-results" src={`${BuildPath()}/images/buttons/x-icon.svg`} alt="Close" />
                    }
                    <AnimatePresence>
                        {userShowSearch && users.length > 0 && (
                            <motion.div
                                className={"search-results search-results-users"}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: '340px' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: aniFade }}
                            >
                                <SimpleBar scrollableNodeProps={{onScroll: (e: any) => scrollHandler(e, false)}} style={{ maxHeight: 530 }}>
                                    <ul style={{maxHeight:386}}>
                                        {users.map(user => {
                                            return (
                                                <UserResult
                                                    user={user}
                                                    selectedUsers={selectedUsers}
                                                    setSelectedUsers={setSelectedUsers}
                                                    key={`user-${user.id}`}
                                                    isAwaiting={false}
                                                />

                                            )
                                        })}
                                    </ul>
                                </SimpleBar>
                            </motion.div>
                        )}
                    </AnimatePresence>

 
                </div>

            </div>

            <div className='results-container'>
                <AnimatePresence>
                    {selectedUsers.length === 0 && selectedGroups.length === 0 && !groupShowSearch && !userShowSearch && (
                        <motion.div
                            className='placeholder'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1}}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0 }}
                        >
                            <p>Please select users or user group(s) this event should be available for.</p>

                            <img className="input-icon" src={`${BuildPath()}/images/planner/people-placeholder.png`} alt="" />
                        </motion.div>
                    )}
                </AnimatePresence>
                <AnimatePresence>
                    {(users.length === 0 && groups.length === 0) && (selectedUsers.length > 0 || selectedGroups.length > 0) && (
                        <motion.div
                            className='search-results search-results-users search-results-groups search-results-all'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1}}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0 }}
                        >
                            <SimpleBar onScroll={(() => {})} style={{ maxHeight: 480 }}>
                                <ul style={{maxHeight:386}}>
                                    {selectedGroups.map(group => {
                                        return (
                                            <GroupResult group={group} selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups} details={true} key={group.id} />
                                        )
                                    })}

                                    {selectedUsers.map(user => {
                                        return (
                                            <UserResult user={user} selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} key={user.id} />
                                        )
                                    })}

                                </ul>
                            </SimpleBar>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Invitees;